import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Center,
  Spinner,
  Icon,
  chakra,
} from '@chakra-ui/react';
import { HiPlus, HiXMark, HiClock } from 'react-icons/hi2';
import {
  Button,
  SelectInput,
  TextareaField,
  DialogComponent,
  StatusBadge,
} from '@themes/aegix';
import { useMyStudents } from '../hooks/useReunification';
import {
  guardianStudentName,
  type GuardianStudentWithLegacyIds,
} from '../services/reunification';
import {
  useCarRiderQueue,
  useJoinCarRiderQueue,
  useUpdateCarRiderQueue,
} from '../hooks/useVisitorManagement';
import { toaster } from './ui/toaster';

// Helper to format ISO date/time
const formatTime = (isoString: string) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

export function CarRiderQueueView() {
  const { t } = useTranslation();
  const { data: studentsData, isLoading: loadingStudents } = useMyStudents();
  const { data: queueData, isLoading: loadingQueue } = useCarRiderQueue();

  const joinMutation = useJoinCarRiderQueue();
  const updateMutation = useUpdateCarRiderQueue();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The real guardian/students payload emits no `id`/`school_id`,
  // so this flow's invalid-data guard fires until a join key ships.
  const students: GuardianStudentWithLegacyIds[] = studentsData?.results ?? [];
  const queueEntries = queueData?.results ?? [];

  const handleOpen = () => {
    if (students.length > 0) {
      setSelectedStudentId(String(students[0].id));
    }
    setNotes('');
    setIsOpen(true);
  };

  const handleLeaveQueue = async (publicId: string) => {
    try {
      await updateMutation.mutateAsync({
        public_id: publicId,
        data: { status: 'cancelled' },
      });
      toaster.create({
        title: t('carRider.leftToastTitle'),
        description: t('carRider.leftToastDesc'),
        type: 'success',
      });
    } catch {
      toaster.create({
        title: t('carRider.leaveErrorTitle'),
        description: t('carRider.leaveErrorDesc'),
        type: 'error',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) {
      toaster.create({
        title: t('carRider.studentRequiredTitle'),
        description: t('carRider.studentRequiredDesc'),
        type: 'error',
      });
      return;
    }

    const student = students.find((s) => String(s.id) === selectedStudentId);
    if (!student || student.school_id === undefined || student.id === undefined) {
      toaster.create({
        title: t('carRider.invalidDataTitle'),
        description: t('carRider.invalidDataDesc'),
        type: 'error',
      });
      return;
    }

    // Check if student is already in queue in non-final state
    const alreadyQueued = queueEntries.some(
      (entry) =>
        entry.student === student.id &&
        (entry.status === 'waiting' || entry.status === 'called'),
    );

    if (alreadyQueued) {
      toaster.create({
        title: t('carRider.alreadyQueuedTitle'),
        description: t('carRider.alreadyQueuedDesc', { name: guardianStudentName(student) }),
        type: 'warning',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await joinMutation.mutateAsync({
        student: student.id,
        school: student.school_id,
        notes,
      });

      toaster.create({
        title: t('carRider.joinedTitle'),
        description: t('carRider.joinedDesc', { name: guardianStudentName(student) }),
        type: 'success',
      });
      setIsOpen(false);
    } catch {
      toaster.create({
        title: t('carRider.joinErrorTitle'),
        description: t('carRider.joinErrorDesc'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const studentOptions = students.map((s) => ({
    label: guardianStudentName(s),
    value: String(s.id),
  }));

  const isLoading = loadingStudents || loadingQueue;

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <Text fontSize="lg" fontWeight="600" color="text.primary">
            {t('carRider.title')}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {t('carRider.subtitle')}
          </Text>
        </Box>
        <Button
          visual="primary"
          onClick={handleOpen}
          icon={<HiPlus />}
          disabled={students.length === 0}
          data-e2e="join-queue-btn"
        >
          {t('carRider.joinBtn')}
        </Button>
      </HStack>

      {isLoading ? (
        <Center py={12}>
          <Spinner size="lg" />
        </Center>
      ) : queueEntries.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {queueEntries.map((entry) => {
            const statusMap: Record<
              string,
              'queued' | 'warning' | 'success' | 'inactive'
            > = {
              waiting: 'queued',
              called: 'warning',
              picked_up: 'success',
              cancelled: 'inactive',
            };
            const badgeStatus = statusMap[entry.status] ?? 'queued';

            return (
              <Box
                key={entry.public_id}
                bg="surface.primary"
                border="1px solid"
                borderColor="border.secondary"
                borderRadius="lg"
                p={5}
                position="relative"
                data-e2e={`queue-card-${entry.public_id}`}
              >
                <HStack justifyContent="space-between" mb={3} alignItems="flex-start">
                  <VStack align="stretch" gap={1}>
                    <Text fontSize="md" fontWeight="600" color="text.primary">
                      {entry.student_name}
                    </Text>
                    <HStack fontSize="xs" color="text.secondary" gap={1.5}>
                      <Icon as={HiClock} />
                      <Text>{t('carRider.queuedAt', { time: formatTime(entry.queued_at || entry.created_at) })}</Text>
                    </HStack>
                  </VStack>
                  <StatusBadge status={badgeStatus} label={entry.status} />
                </HStack>

                {entry.notes && (
                  <Box bg="surface.secondary" p={3} borderRadius="md" mb={4}>
                    <Text fontSize="xs" fontWeight="600" color="text.secondary" mb={1}>
                      {t('carRider.notes')}
                    </Text>
                    <Text fontSize="sm" color="text.primary">
                      {entry.notes}
                    </Text>
                  </Box>
                )}

                {(entry.status === 'waiting' || entry.status === 'called') && (
                  <HStack justifyContent="flex-end" mt={2}>
                    <Button
                      visual="secondary"
                      size="sm"
                      icon={<HiXMark />}
                      onClick={() => handleLeaveQueue(entry.public_id)}
                      data-e2e={`leave-queue-${entry.public_id}`}
                    >
                      {t('carRider.leaveBtn')}
                    </Button>
                  </HStack>
                )}
              </Box>
            );
          })}
        </SimpleGrid>
      ) : (
        <Center py={12} border="1px dashed" borderColor="border.secondary" borderRadius="lg">
          <VStack gap={2}>
            <HiClock size={32} />
            <Text fontSize="md" fontWeight="600" color="text.secondary">
              {t('carRider.empty')}
            </Text>
            <Text fontSize="sm" color="text.secondary" textAlign="center" maxW="400px">
              {students.length === 0
                ? t('carRider.noStudentsNotice')
                : t('carRider.notInQueue')}
            </Text>
          </VStack>
        </Center>
      )}

      <DialogComponent
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        headingText={t('carRider.title')}
        e2eTitle="join-queue-dialog"
      >
        <form onSubmit={handleSubmit}>
          <VStack gap={4} align="stretch" mt={4}>
            <SelectInput
              label={t('carRider.selectStudent')}
              name="student"
              option={studentOptions}
              value={selectedStudentId}
              onChange={setSelectedStudentId}
              e2eTitle="queue-student-select"
              searchPlaceholder={t('searchPlaceholder')}
              required
            />

            <Box>
              <chakra.label fontSize="sm" color="text.secondary" mb={1} display="block">
                {t('carRider.notesLabel')}
              </chakra.label>
              <TextareaField
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('carRider.notesPlaceholder')}
                data-e2e="queue-notes-input"
              />
            </Box>

            <HStack justifyContent="flex-end" gap={3} mt={4}>
              <Button visual="secondary" onClick={() => setIsOpen(false)} type="button">
                {t('carRider.cancel')}
              </Button>
              <Button visual="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('carRider.joining') : t('carRider.joinBtn')}
              </Button>
            </HStack>
          </VStack>
        </form>
      </DialogComponent>
    </Box>
  );
}
